import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { parse } from "date-fns"
import { logDatadogRum } from "../../common/utils/datadog"
import { useI18n } from "../../common/utils/i18n-context"
import { selectorOf } from "../../common/utils/redux-helpers"
import {
    AdvancedPauseDuration,
    defaultPauseData,
    PauseDurationUnits,
    PausePageStatus,
    PauseState,
    PauseStatus,
    PauseSubmissionStatus,
} from "./pause-model"

export const initialPauseStatus: PauseStatus = {
    pauseData: defaultPauseData,
    isPaused: false,
}

export const initialSelectedDuration: AdvancedPauseDuration = {
    duration: 3,
    durationUnits: PauseDurationUnits.MONTHS,
}

export const initialPauseState: PauseState = {
    pauseStatus: initialPauseStatus,
    pageStatus: PausePageStatus.LOADING_PAUSE_STATUS,
    submissionStatus: PauseSubmissionStatus.SUBMITTING,
}

export const pauseSlice = createSlice({
    name: "pause",
    initialState: initialPauseState,
    reducers: {
        resetState: () => initialPauseState,
        resetPauseStatus(state) {
            state.pauseStatus = initialPauseStatus
        },
        updatePauseStatus(state, action: PayloadAction<PauseStatus>) {
            state.pauseStatus = action.payload
        },
        updatePausePageStatus(state, action: PayloadAction<PausePageStatus>) {
            state.pageStatus = action.payload
        },
        updatePauseSubmissionStatus(state, action: PayloadAction<PauseSubmissionStatus>) {
            state.submissionStatus = action.payload
        },
    },
})

export const durationUnitsAsString = (pauseDuration: AdvancedPauseDuration): string => {
    const { duration, durationUnits } = pauseDuration
    const { gettext } = useI18n()
    // Not using ngettext as currently there's a clash between singular and plural from indeed:dradis-common translations for day, week, month
    // This results in some bad translations in polish as they have 2 plural forms. Change to use ngettext when possible
    switch (durationUnits) {
        case PauseDurationUnits.DAYS:
            return duration === 1 ? gettext("day") : gettext("days")
        case PauseDurationUnits.WEEKS:
            return duration === 1 ? gettext("week") : gettext("weeks")
        case PauseDurationUnits.MONTHS:
            return duration === 1 ? gettext("month") : gettext("months")
        default:
            logDatadogRum(Error("Invalid pause duration units selected"))
            return duration === 1 ? gettext("month") : gettext("months")
    }
}

export const isPausedSelector = selectorOf((state) => state.pause.pauseStatus.isPaused)
export const pausePageStatusSelector = selectorOf((state) => state.pause.pageStatus)
export const pauseDataSelector = selectorOf((state) => state.pause.pauseStatus.pauseData)
export const pauseStatusSelector = selectorOf((state) => state.pause.pauseStatus)
export const selectedDurationUnitsAsString = selectorOf((state) => {
    const { duration, durationUnits } = state.pause.pauseStatus.pauseData
    return durationUnitsAsString({ duration, durationUnits })
})
export const effectiveDateSelector = selectorOf((state) => {
    const { endTimeSeconds = 0 } = state.pause.pauseStatus.pauseData || {}
    return parse(endTimeSeconds.toString(), "t", new Date())
})

export const pauseSubmissionStatusSelector = selectorOf((state) => state.pause.submissionStatus)
